import { useEffect, useState } from "react";
import type { CompanyType } from "@services/licensing/types";
import type {
  FieldPathByValue,
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from "react-hook-form";
import { VENDOR_COMPANY_TYPE_CODE } from "../constants";

const EMPTY_ARRAY: string[] = [];

const isSameArray = (arr1: string[], arr2: string[]) =>
  arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index]);

export function useSyncCompanyType<
  TForm extends FieldValues,
  TField extends FieldPathByValue<TForm, string[]>,
>(
  companyTypes: CompanyType[],
  form: UseFormReturn<TForm>,
  isCompanyTypesLoading: boolean,
  isCompanyTypesError: boolean,
) {
  const companyTypesFieldName: Path<TForm> = "companyTypes" as Path<TForm>;
  const [isVendorAutoSelected, setIsVendorAutoSelected] = useState<boolean | null>(null);
  const selectedCompanyTypes: string[] = form.watch(companyTypesFieldName);

  useEffect(() => {
    if (isCompanyTypesLoading || isCompanyTypesError) return;
    // remove old invalid values from the selected company types
    let newCompanyTypes = selectedCompanyTypes.filter((companyTypeCode) =>
      companyTypes.some((companyType) => companyType.companyTypeCode === companyTypeCode),
    );

    // automatically select or deselect the vendor company type if applicable
    if (
      companyTypes.some((companyType) => companyType.companyTypeCode === VENDOR_COMPANY_TYPE_CODE)
    ) {
      if (isVendorAutoSelected === null && newCompanyTypes.length > 0) {
        if (!newCompanyTypes.includes(VENDOR_COMPANY_TYPE_CODE)) {
          setIsVendorAutoSelected(true);
          newCompanyTypes = [VENDOR_COMPANY_TYPE_CODE, ...newCompanyTypes];
        } else {
          setIsVendorAutoSelected(false);
        }
      }

      if (isVendorAutoSelected && newCompanyTypes.length === 1) {
        newCompanyTypes = EMPTY_ARRAY;
      }
      if (newCompanyTypes.length === 0) {
        setIsVendorAutoSelected(null);
      }
    }

    // if the new company types array has changed, update the form value
    if (!isSameArray(newCompanyTypes, selectedCompanyTypes))
      form.setValue(companyTypesFieldName, newCompanyTypes as PathValue<TForm, TField>, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
  }, [
    form,
    selectedCompanyTypes,
    companyTypes,
    companyTypesFieldName,
    isVendorAutoSelected,
    isCompanyTypesLoading,
    isCompanyTypesError,
  ]);

  const isVendorDisabled =
    selectedCompanyTypes.length >= 2 && selectedCompanyTypes.includes(VENDOR_COMPANY_TYPE_CODE);

  return { isVendorDisabled: isVendorAutoSelected || isVendorDisabled };
}
